import { assertHasOwnProperties, assertInArray, assertArray } from '../validators';
import { Config } from '../video-data/config';
import { AnimationCurveGroup } from './AnimationCurveGroup';
import type { CurveDescription } from './AnimationCurve';
import type { RemoveAnchoredPoints } from './Curve';

type Container = {
    duration: number;
};

export type SerializedAnimationConfig = {
    name?: string;
    mode: 'single' | 'multi';
    duration?: number;
    offsetMode?: 'property' | 'object';
    percentageOffset?: number;
    initPercentageOffset?: number;
    mirrorPercentageOffset?: boolean;
    mirrorOffsetIncrement?: boolean;
    shouldOffsetPropertyValue?: boolean | boolean[];
    zigZagPerLine?: boolean;
    zigZagPerLineStart?: string;
    removeAnchoredPoints?: RemoveAnchoredPoints;
    drivenRange: [number, number] | [number, number][];
    drivenProperty: string[];
    curve: CurveDescription;
};

export type AnimationConfig = {
    drivenObjects: any[];
} & SerializedAnimationConfig;

export class AnimationCurveManager {
    classType = 'AnimationCurveManager';
    name = 'animationCurveManager_1';
    container: Container = null;
    index = 0;
    _percentage = 0;
    groups: AnimationCurveGroup[] = [];
    names: string[] = [];

    maxDuration = -1;
    limitDuration = false;
    alignToBounds = false;

    #initIntroStart = -1;
    #initIntroDuration = -1;
    #initOutroStart = -1;
    #initOutroDuration = -1;

    constructor(container: Container) {
        this.container = container;
    }

    /**
     * This method stores the current intro and outro, start and duration values as initialization values.
     * ResetToInitValues uses these values to reset the manager to its initialized state and helps recover the original
     * animation after applying several transformation operations such as move and scale.
     */
    setInitValues() {
        if (this.isEmpty()) {
            return;
        }
        this.#initIntroStart = this.smallestX(Config.START_ANIMATION_CURVE_ANCHOR);
        this.#initIntroDuration = this.introDuration;
        this.#initOutroStart = this.smallestX(Config.END_ANIMATION_CURVE_ANCHOR);
        this.#initOutroDuration = this.outroDuration;
    }

    get _initIntroDuration() {
        return this.#initIntroDuration;
    }

    /**
     * This method restores the animation back to its initialized state.
     */
    resetToInitValues() {
        // outwards
        this.#resetIntroStart();
        this.#resetIntroDuration();

        // inwards
        // here we have to scale the animation back to its original length before we can move it. That's because we
        // scale from the end and move from the start
        this.#resetOutroDuration();
        this.#resetOutroStart();
    }

    #resetIntroStart() {
        if (this.#initIntroStart === -1) {
            return;
        }
        const introStart = this.smallestX(Config.START_ANIMATION_CURVE_ANCHOR);
        if (this.#initIntroStart === this.introStart) {
            return;
        }
        const introOffset = this.#initIntroStart - introStart;
        this.moveAllGroups(introOffset, 0, Config.START_ANIMATION_CURVE_ANCHOR);
    }

    #resetIntroDuration() {
        if (this.#initIntroDuration === -1) {
            return;
        }
        const introDuration = this.introDuration;
        if (this.#initIntroDuration === introDuration) {
            return;
        }
        if (introDuration === 0) {
            return;
        }
        const introScale = this.#initIntroDuration / introDuration;
        this.scaleAllGroups(introScale, Config.START_ANIMATION_CURVE_ANCHOR);
    }

    #resetOutroStart() {
        if (this.#initOutroStart === -1) {
            return;
        }
        const outroStart = this.smallestX(Config.END_ANIMATION_CURVE_ANCHOR);
        if (this.#initOutroStart === outroStart) {
            return;
        }
        const outroOffset = outroStart - this.#initOutroStart;
        this.moveAllGroups(-outroOffset, 0, Config.END_ANIMATION_CURVE_ANCHOR);
    }

    #resetOutroDuration() {
        if (this.#initOutroDuration === -1) {
            return;
        }
        const outroDuration = this.outroDuration;
        if (this.#initOutroDuration === outroDuration) {
            return;
        }
        if (outroDuration === 0) {
            return;
        }
        const outroScale = this.#initOutroDuration / outroDuration;
        this.scaleAllGroups(outroScale, Config.END_ANIMATION_CURVE_ANCHOR);
    }

    /**
     * Returns a unique name for name
     */
    #getNextName(name: string): string {
        let nextName = name;
        let counter = 0;
        while (this.names.indexOf(nextName) !== -1) {
            nextName = name + counter;
            counter += 1;
        }
        return nextName;
    }

    destroy() {
        this.clear();
    }

    clear() {
        this.groups.forEach((group) => group.clear());
        this.groups = [];
        this.names = [];
    }

    hasName(name: string): boolean {
        const index = this.names.indexOf(name);
        if (index !== -1) {
            return true;
        }
        return false;
    }

    isEmpty(): boolean {
        return this.groups.every((g) => g.isEmpty());
    }

    /**
     * Total duration
     */
    get duration(): number {
        return this.container.duration;
    }

    /**
     * The time in milliseconds the intro starts
     */
    get introStart(): number {
        return Math.trunc(this.smallestX(Config.START_ANIMATION_CURVE_ANCHOR) * this.duration);
    }

    /**
     * The time in milliseconds the intro ends
     */
    get introEnd(): number {
        // Here we only measure from 0 to the largest value in x. It is possible
        // that we have keys that have a negative x value. What are we going to
        // do about these?
        return Math.trunc(this.largestX(Config.START_ANIMATION_CURVE_ANCHOR) * this.duration);
    }

    get introDuration(): number {
        return this.introEnd - this.introStart;
    }

    /**
     * The time in milliseconds the outro starts
     */
    get outroStart(): number {
        // Here we only measure from the smallest value in x to 1. It is possible
        // that we have keys that have a value higher then 1. What are we going to
        // do about these?
        return Math.trunc(this.smallestX(Config.END_ANIMATION_CURVE_ANCHOR) * this.duration);
    }

    /**
     * The time in milliseconds the outro ends
     */
    get outroEnd(): number {
        // Here we only measure from the smallest value in x to 1. It is possible
        // that we have keys that have a value higher then 1. What are we going to
        // do about these?
        return Math.trunc(this.largestX(Config.END_ANIMATION_CURVE_ANCHOR) * this.duration);
    }

    get outroDuration(): number {
        return this.outroEnd - this.outroStart;
    }

    hasAnchors(anchor = Config.START_ANIMATION_CURVE_ANCHOR) {
        return this.groups.some((g) => {
            return g.hasAnchors(anchor);
        });
    }

    // 0 to 1
    set percentage(value: number) {
        this._percentage = Math.min(Math.max(value, 0), 1);
        this._evalPercentage();
    }

    get percentage(): number {
        return this._percentage;
    }

    get currentTime(): number {
        return this.percentage * this.duration;
    }

    reset() {
        this.groups.forEach((group) => group.reset());
    }

    _evalPercentage() {
        this.groups.forEach((group) => {
            group.percentage = this.percentage;
        });
    }

    smallestX(anchors = Config.ALL_ANIMATION_CURVE_ANCHOR): number {
        if (!this.hasAnchors(anchors)) {
            return 0;
        }
        return Math.min(...this.groups.map((g) => g.smallestX(anchors)));
    }

    largestX(anchors = Config.ALL_ANIMATION_CURVE_ANCHOR): number {
        if (!this.hasAnchors(anchors)) {
            return 0; // it's strange to return 0 here, it might be better to return -1 to indicate we don't have any
        }
        return Math.max(...this.groups.map((g) => g.largestX(anchors)));
    }

    scaleAllGroups(scaleFactor: number, anchors = Config.ALL_ANIMATION_CURVE_ANCHOR) {
        this.groups.forEach((g) => {
            g.scaleGroup(scaleFactor, anchors);
        });
    }

    moveAllGroups(deltaX: number, deltaY: number, anchors = Config.ALL_ANIMATION_CURVE_ANCHOR) {
        this.groups.forEach((g) => {
            g.moveGroup(deltaX, deltaY, anchors);
        });
    }

    /**
     * config = {
     *     "mode": "single",
     *     "percentageOffset": 0.0,
     *     "mirrorPercentageOffset": false,
     *     "curve": {
     *         "duration":4,
     *         "classType": "BezierCurve",
     *         "points":[
     *             {"anchor":"start","position":{"x":0,"y":0},"out":{"distance":0.00523,"angle":0.09967}},
     *             {"anchor":"end","position":{"x":0.81718,"y":1},"in":{"distance":0.03071,"angle":-3.14127},"out":{"distance":0.0907,"angle":0}},
     *             {"anchor":"end","position":{"x":1,"y":0},"in":{"distance":0.00523,"angle":3.04192}},
     *         ]
     *     },
     *     "drivenRange": [0.5, 1],
     *     "drivenProperty": ["end1"],
     *     "drivenObjects": [object],
     *     "shouldOffsetPropertyValue": false
     * }
     **/
    add(partialConfig: Partial<AnimationConfig>, overrides: Partial<AnimationConfig> = {}) {
        const config = Object.assign({}, partialConfig, overrides);

        assertHasOwnProperties(config, ['mode', 'curve', 'drivenObjects', 'drivenRange']);
        assertInArray(config.mode, ['single', 'multi']);
        assertArray(config.drivenObjects);
        assertArray(config.drivenRange);

        const name = config.hasOwnProperty('name') ? config.name : 'singleCurveGroup';
        const uniqueName = this.#getNextName(name);
        config.name = uniqueName;
        config.duration = config.hasOwnProperty('duration') ? config.duration : this.duration;
        config.offsetMode = config.hasOwnProperty('offsetMode') ? config.offsetMode : 'object';
        config.mirrorPercentageOffset = config.hasOwnProperty('mirrorPercentageOffset')
            ? config.mirrorPercentageOffset
            : false;
        config.mirrorOffsetIncrement = config.hasOwnProperty('mirrorOffsetIncrement')
            ? config.mirrorOffsetIncrement
            : false;
        config.shouldOffsetPropertyValue = config.hasOwnProperty('shouldOffsetPropertyValue')
            ? config.shouldOffsetPropertyValue
            : false;
        config.zigZagPerLine = config.hasOwnProperty('zigZagPerLine')
            ? config.zigZagPerLine
            : false;
        config.zigZagPerLineStart = config.hasOwnProperty('zigZagPerLineStart')
            ? config.zigZagPerLineStart
            : Config.ZIG_ZAG_START_LEFT;

        if (config.hasOwnProperty('removeAnchoredPoints')) {
            config.curve.removeAnchoredPoints = config.removeAnchoredPoints;
            delete config.removeAnchoredPoints;
        }

        let curveGroup: AnimationCurveGroup;
        if (config.mode === 'single') {
            curveGroup = AnimationCurveGroup.single(config);
        } else {
            curveGroup = AnimationCurveGroup.multi(config);
        }

        this.groups.push(curveGroup);
        this.names.push(uniqueName);

        return curveGroup;
    }

    /**
     * @internal
     */
    _getDescription() {
        const description = {
            limitDuration: this.limitDuration,
            maxDuration: this.maxDuration,
            groups: {},
        };
        this.groups.forEach((group, index) => {
            description.groups[this.names[index]] = group._getDescription();
        });
        return description;
    }
}
